import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Divider, Input, InputNumber, Select, Space, Switch, Typography, message } from 'antd';
import useSceneStore from '../../stores/sceneStore';
import { useSelectionStore } from '../../stores/selectionStore';
import usePluginStore from '../../plugins/plugin-manager';
import { reportComponentUsage } from '../../plugins/plugin-api';
import { ensurePaidFeature } from '../../utils/entitlement';
import { chartTemplatesOf, presetPriceLabel, templateToChartDrafts } from '../../utils/marketPresets';
import { suggestCharts } from '../../utils/chart3dAi';
import { createChart3DObject, type Chart3DKind, type Chart3DObject } from '../../types/chart3d';
import { parseJson } from '../charts/utils';
import DataSourceConfigForm from '../dashboard/DataSourceConfigForm';

const { Text } = Typography;

function VectorInput({
  label,
  value,
  onChange,
  testPrefix,
}: {
  label: string;
  value: [number, number, number];
  onChange: (value: [number, number, number]) => void;
  testPrefix: string;
}) {
  return (
    <div>
      <Text style={{ fontSize: 12, color: '#888' }}>{label}</Text>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 4 }}>
        {(['X', 'Y', 'Z'] as const).map((axis, i) => (
          <InputNumber
            key={axis}
            data-testid={`${testPrefix}-${axis.toLowerCase()}`}
            size="small"
            addonBefore={axis}
            step={0.5}
            style={{ width: '100%' }}
            value={value[i]}
            onChange={(v) => {
              const next = [...value] as [number, number, number];
              next[i] = v ?? 0;
              onChange(next);
            }}
          />
        ))}
      </div>
    </div>
  );
}

function AddChartButtons({ onAdd }: { onAdd: (kind: Chart3DKind) => void }) {
  return (
    <Space wrap size={6}>
      <Button data-testid="chart3d-add-bar3d" size="small" onClick={() => onAdd('bar3d')}>+ 柱状图</Button>
      <Button data-testid="chart3d-add-scatter3d" size="small" onClick={() => onAdd('scatter3d')}>+ 散点图</Button>
      <Button data-testid="chart3d-add-surface3d" size="small" onClick={() => onAdd('surface3d')}>+ 曲面图</Button>
      <Button data-testid="chart3d-add-line3d" size="small" onClick={() => onAdd('line3d')}>+ 折线图</Button>
      <Button data-testid="chart3d-add-pie3d" size="small" onClick={() => onAdd('pie3d')}>+ 饼图</Button>
      <Button data-testid="chart3d-add-donut3d" size="small" onClick={() => onAdd('donut3d')}>+ 环形图</Button>
      <Button data-testid="chart3d-add-gauge3d" size="small" onClick={() => onAdd('gauge3d')}>+ 仪表盘</Button>
      <Button data-testid="chart3d-add-radar3d" size="small" onClick={() => onAdd('radar3d')}>+ 雷达图</Button>
      <Button data-testid="chart3d-add-funnel3d" size="small" onClick={() => onAdd('funnel3d')}>+ 漏斗图</Button>
      <Button data-testid="chart3d-add-board3d" size="small" onClick={() => onAdd('board3d')}>+ 贴屏看板</Button>
      <Button data-testid="chart3d-add-map3d" size="small" onClick={() => onAdd('map3d')}>+ 3D 地图</Button>
      <Button data-testid="chart3d-add-globe3d" size="small" onClick={() => onAdd('globe3d')}>+ 3D 地球</Button>
      <Button data-testid="chart3d-add-graph3d" size="small" onClick={() => onAdd('graph3d')}>+ 关系图</Button>
    </Space>
  );
}

function ChartTemplateList({
  templates,
  onApply,
}: {
  templates: ReturnType<typeof chartTemplatesOf>;
  onApply: (item: ReturnType<typeof chartTemplatesOf>[number]) => void;
}) {
  if (!templates.length) {
    return <Text style={{ fontSize: 11, color: '#666' }}>市场暂无 3D 图表模板</Text>;
  }
  return (
    <Space wrap size={6}>
      {templates.map((item) => {
        const price = presetPriceLabel(item);
        return (
          <Button
            key={item.id}
            data-testid={`chart3d-template-${item.id}`}
            size="small"
            onClick={() => onApply(item)}
          >
            {item.name}
            <span style={{ marginLeft: 6, fontSize: 10, color: price.color }}>{price.text}</span>
          </Button>
        );
      })}
    </Space>
  );
}

export default function Chart3DPanel() {
  const charts = useSceneStore((s) => s.chart3dObjects);
  const selectedId = useSelectionStore((s) => s.chart3dSelectedId);
  const addChart3D = useSceneStore((s) => s.addChart3D);
  const updateChart3D = useSceneStore((s) => s.updateChart3D);
  const removeChart3D = useSceneStore((s) => s.removeChart3D);
  const chart = useMemo(() => charts.find((item) => item.id === selectedId) ?? null, [charts, selectedId]);
  const marketComponents = usePluginStore((s) => s.marketComponents);
  const chartTemplates = useMemo(() => chartTemplatesOf(marketComponents), [marketComponents]);
  const [dataSourceText, setDataSourceText] = useState('');
  const [dataSourceError, setDataSourceError] = useState<string | null>(null);
  const [showDataSourceForm, setShowDataSourceForm] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');

  const applyChartTemplate = async (item: (typeof chartTemplates)[number]) => {
    if (item.entitled === false) {
      if (item.priceType === 'credits') {
        message.warning(`积分不足：该模板需 ${Number(item.creditPrice ?? 0)} 积分/次`);
      } else {
        message.warning('该图表模板需开通对应套餐后使用');
        useSceneStore.getState().selectModel(null);
        useSelectionStore.getState().deselectAll();
        void ensurePaidFeature('chart3d');
      }
      return;
    }
    const paid = await ensurePaidFeature('chart3d');
    if (!paid) return;
    try {
      const drafts = templateToChartDrafts(item);
      const ids = drafts.map((draft) => addChart3D(draft));
      const last = ids[ids.length - 1];
      useSceneStore.getState().selectModel(null);
      if (last) useSelectionStore.getState().selectChart3D(last);
      message.success(`已应用图表模板「${item.name}」（${drafts.length} 个图表）`);
      if (item.priceType === 'paid' || item.priceType === 'credits') {
        void reportComponentUsage(item.id).catch(() => {});
      }
    } catch (error) {
      message.error(`应用失败：${(error as Error).message}`);
    }
  };

  useEffect(() => {
    setDataSourceText(chart?.dataSource ? JSON.stringify(chart.dataSource, null, 2) : '');
    setDataSourceError(null);
  }, [chart?.id]);

  const applyAi = () => {
    const suggestions = suggestCharts(aiPrompt);
    if (!suggestions.length) {
      message.warning('请先描述想要的图表，例如「按地区看销售占比和趋势」');
      return;
    }
    const start = charts.length;
    const ids: string[] = [];
    suggestions.forEach((suggestion, index) => {
      const slot = start + index;
      ids.push(addChart3D(createChart3DObject({
        kind: suggestion.kind,
        name: suggestion.name,
        position: [(slot % 3 - 1) * 10, 0, Math.floor(slot / 3) * 10],
        demo: { count: suggestion.kind === 'graph3d' ? 18 : suggestion.kind === 'gauge3d' ? 1 : 120, seed: Date.now() % 100000 + index },
      })));
    });
    if (ids[0]) {
      useSceneStore.getState().selectModel(null);
      useSelectionStore.getState().selectChart3D(ids[ids.length - 1]);
    }
    message.success(`AI 已生成 ${suggestions.length} 个图表：${suggestions.map((s) => s.reason).join(' / ')}`);
  };

  const handleAdd = (kind: Chart3DKind) => {
    const names: Record<Chart3DKind, string> = {
      bar3d: '3D 柱状图',
      scatter3d: '3D 散点图',
      surface3d: '3D 曲面图',
      line3d: '3D 折线图',
      pie3d: '3D 饼图',
      donut3d: '3D 环形图',
      gauge3d: '3D 仪表盘',
      radar3d: '3D 雷达图',
      funnel3d: '3D 漏斗图',
      board3d: '3D 贴屏看板',
      map3d: '3D 地图',
      globe3d: '3D 地球',
      graph3d: '3D 关系图',
    };
    const counts: Record<Chart3DKind, number> = {
      bar3d: 200,
      scatter3d: 120,
      surface3d: 144,
      line3d: 30,
      pie3d: 5,
      donut3d: 5,
      gauge3d: 1,
      radar3d: 6,
      funnel3d: 5,
      board3d: 8,
      map3d: 1,
      globe3d: 24,
      graph3d: 18,
    };
    const index = charts.length;
    const slots: Array<[number, number, number]> = [[0, 0, 0], [10, 0, 0], [-10, 0, 0], [0, 0, -10]];
    const slot = slots[index % slots.length];
    const ring = Math.floor(index / slots.length);
    const position: [number, number, number] = [slot[0], 0, slot[2] - ring * 10];
    const id = addChart3D(createChart3DObject({
      kind,
      name: names[kind],
      position,
      demo: { count: counts[kind], seed: Date.now() % 100000 },
    }));
    useSceneStore.getState().selectModel(null);
    useSelectionStore.getState().selectChart3D(id);
  };

  if (!chart) {
    return (
      <div data-testid="chart3d-panel-empty" style={{ padding: 20, color: '#888' }}>
        <div style={{ marginBottom: 12, textAlign: 'center' }}>暂无选中的 3D 图表</div>
        <div style={{ marginBottom: 8, fontSize: 12 }}>新增后可直接在画布中移动/旋转/缩放：</div>
        <AddChartButtons onAdd={handleAdd} />
        <div style={{ marginTop: 14 }}>
          <Text style={{ fontSize: 12, color: '#888' }}>市场图表模板</Text>
          <div style={{ marginTop: 6 }}>
            <ChartTemplateList templates={chartTemplates} onApply={(item) => void applyChartTemplate(item)} />
          </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <Text style={{ fontSize: 12, color: '#888' }}>AI 生成图表</Text>
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <Input
              data-testid="chart3d-ai-prompt"
              size="small"
              placeholder="例如：按地区看销售占比和趋势"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              onPressEnter={applyAi}
            />
            <Button data-testid="chart3d-ai-generate" size="small" type="primary" onClick={applyAi}>生成</Button>
          </div>
        </div>
        <div style={{ marginTop: 12, fontSize: 12, lineHeight: 1.7 }}>
          也可以在场景树「3D 图表」或画布中选中已有图表。
        </div>
      </div>
    );
  }

  const update = (patch: Partial<Chart3DObject>, recordHistory = false) => {
    updateChart3D(chart.id, patch, recordHistory);
  };

  const updateBar = (patch: Partial<Chart3DObject['bar']>) => {
    update({ bar: { ...chart.bar, ...patch } });
  };

  const applyDataSource = () => {
    const parsed = parseJson<Record<string, unknown> | null>(dataSourceText, null);
    if (dataSourceText.trim() && (!parsed || typeof parsed !== 'object')) {
      setDataSourceError('数据源 JSON 无效');
      return;
    }
    setDataSourceError(null);
    update({ dataSource: (parsed ?? undefined) as Chart3DObject['dataSource'] }, true);
  };

  return (
    <div data-testid="chart3d-panel" style={{ padding: '8px 12px', overflow: 'auto', height: '100%' }}>
      <Space orientation="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Text style={{ fontSize: 12, color: '#888' }}>名称</Text>
          <Input
            data-testid="chart3d-name"
            size="small"
            value={chart.name}
            onChange={(e) => update({ name: e.target.value })}
          />
        </div>

        <div>
          <Text style={{ fontSize: 12, color: '#888' }}>图表类型</Text>
          <Select
            data-testid="chart3d-kind"
            size="small"
            style={{ width: '100%' }}
            value={chart.kind}
            options={[
              { value: 'bar3d', label: '3D 柱状图' },
              { value: 'scatter3d', label: '3D 散点图' },
              { value: 'surface3d', label: '3D 曲面图' },
              { value: 'line3d', label: '3D 折线图' },
              { value: 'pie3d', label: '3D 饼图' },
              { value: 'donut3d', label: '3D 环形图' },
              { value: 'gauge3d', label: '3D 仪表盘' },
              { value: 'radar3d', label: '3D 雷达图' },
              { value: 'funnel3d', label: '3D 漏斗图' },
              { value: 'board3d', label: '3D 贴屏看板' },
              { value: 'map3d', label: '3D 地图' },
              { value: 'globe3d', label: '3D 地球' },
              { value: 'graph3d', label: '3D 关系图' },
            ]}
            onChange={(value) => update({ kind: value as Chart3DKind }, true)}
          />
        </div>

        <div>
          <Text style={{ fontSize: 12, color: '#888' }}>新增图表</Text>
          <div style={{ marginTop: 4 }}>
            <AddChartButtons onAdd={handleAdd} />
          </div>
        </div>

        <div>
          <Text style={{ fontSize: 12, color: '#888' }}>市场图表模板</Text>
          <div style={{ marginTop: 4 }}>
            <ChartTemplateList templates={chartTemplates} onApply={(item) => void applyChartTemplate(item)} />
          </div>
        </div>

        <div>
          <Text style={{ fontSize: 12, color: '#888' }}>AI 生成图表</Text>
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            <Input
              data-testid="chart3d-ai-prompt"
              size="small"
              placeholder="例如：按地区看销售占比和趋势"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              onPressEnter={applyAi}
            />
            <Button data-testid="chart3d-ai-generate" size="small" type="primary" onClick={applyAi}>生成</Button>
          </div>
        </div>

        <div>
          <Text style={{ fontSize: 12, color: '#888' }}>性能档</Text>
          <Select
            data-testid="chart3d-perf-tier"
            size="small"
            style={{ width: '100%' }}
            value={chart.perfTier ?? 'high'}
            options={[
              { value: 'high', label: '高（完整效果）' },
              { value: 'medium', label: '中（标签≤20）' },
              { value: 'low', label: '低（标签≤5 / 关动画）' },
            ]}
            onChange={(value) => update({ perfTier: value as Chart3DObject['perfTier'] }, true)}
          />
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Text style={{ fontSize: 12, color: '#888' }}>可见</Text>
          <Switch size="small" checked={chart.visible} onChange={(v) => update({ visible: v }, true)} />
          <Text style={{ fontSize: 12, color: '#888' }}>锁定</Text>
          <Switch size="small" checked={!!chart.locked} onChange={(v) => update({ locked: v }, true)} />
        </div>

        <VectorInput
          label="位置 (m)"
          value={chart.position}
          onChange={(position) => update({ position })}
          testPrefix="chart3d-position"
        />
        <VectorInput
          label="旋转 (°)"
          value={chart.rotation}
          onChange={(rotation) => update({ rotation })}
          testPrefix="chart3d-rotation"
        />
        <VectorInput
          label="缩放"
          value={chart.scale}
          onChange={(scale) => update({ scale })}
          testPrefix="chart3d-scale"
        />

        {chart.kind === 'bar3d' && (
          <>
        <Divider style={{ margin: '4px 0' }} />
        <Text strong style={{ fontSize: 12 }}>柱体</Text>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>每行列数</Text>
            <InputNumber
              data-testid="chart3d-bar-cols"
              size="small"
              min={1}
              max={100}
              style={{ width: '100%' }}
              value={chart.bar.cols}
              onChange={(v) => updateBar({ cols: Math.max(1, v ?? 1) })}
            />
          </div>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>柱间距</Text>
            <InputNumber
              size="small"
              min={0.1}
              step={0.1}
              style={{ width: '100%' }}
              value={chart.bar.spacing}
              onChange={(v) => updateBar({ spacing: Math.max(0.1, v ?? 0.8) })}
            />
          </div>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>柱宽/深</Text>
            <InputNumber
              size="small"
              min={0.05}
              step={0.05}
              style={{ width: '100%' }}
              value={chart.bar.barSize}
              onChange={(v) => updateBar({ barSize: Math.max(0.05, v ?? 0.55) })}
            />
          </div>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>最大高度</Text>
            <InputNumber
              size="small"
              min={0.5}
              step={0.5}
              style={{ width: '100%' }}
              value={chart.bar.maxHeight}
              onChange={(v) => updateBar({ maxHeight: Math.max(0.5, v ?? 6) })}
            />
          </div>
        </div>

        <div>
          <Text style={{ fontSize: 11, color: '#888' }}>调色板（逗号分隔）</Text>
          <Input
            size="small"
            value={chart.bar.palette.join(',')}
            onChange={(e) => updateBar({ palette: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>不透明度</Text>
            <InputNumber
              size="small"
              min={0.05}
              max={1}
              step={0.05}
              style={{ width: '100%' }}
              value={chart.bar.opacity}
              onChange={(v) => updateBar({ opacity: v ?? 1 })}
            />
          </div>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>金属度</Text>
            <InputNumber
              size="small"
              min={0}
              max={1}
              step={0.05}
              style={{ width: '100%' }}
              value={chart.bar.metalness}
              onChange={(v) => updateBar({ metalness: v ?? 0 })}
            />
          </div>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>粗糙度</Text>
            <InputNumber
              size="small"
              min={0}
              max={1}
              step={0.05}
              style={{ width: '100%' }}
              value={chart.bar.roughness}
              onChange={(v) => updateBar({ roughness: v ?? 1 })}
            />
          </div>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>自发光色</Text>
            <Input
              size="small"
              value={chart.bar.emissive}
              onChange={(e) => updateBar({ emissive: e.target.value })}
            />
          </div>
        </div>
          </>
        )}

        {chart.kind === 'scatter3d' && (
          <>
            <Divider style={{ margin: '4px 0' }} />
            <Text strong style={{ fontSize: 12 }}>散点</Text>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>点大小</Text>
                <InputNumber size="small" min={0.05} step={0.05} style={{ width: '100%' }} value={chart.scatter.pointSize} onChange={(v) => update({ scatter: { ...chart.scatter, pointSize: v ?? 0.28 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>分布范围</Text>
                <InputNumber size="small" min={1} step={1} style={{ width: '100%' }} value={chart.scatter.spread} onChange={(v) => update({ scatter: { ...chart.scatter, spread: v ?? 12 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>不透明度</Text>
                <InputNumber size="small" min={0.05} max={1} step={0.05} style={{ width: '100%' }} value={chart.scatter.opacity} onChange={(v) => update({ scatter: { ...chart.scatter, opacity: v ?? 1 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>调色板</Text>
                <Input size="small" value={chart.scatter.palette.join(',')} onChange={(e) => update({ scatter: { ...chart.scatter, palette: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) } })} />
              </div>
            </div>
          </>
        )}

        {chart.kind === 'surface3d' && (
          <>
            <Divider style={{ margin: '4px 0' }} />
            <Text strong style={{ fontSize: 12 }}>曲面</Text>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>网格密度</Text>
                <InputNumber size="small" min={4} max={64} style={{ width: '100%' }} value={chart.surface.gridSize} onChange={(v) => update({ surface: { ...chart.surface, gridSize: Math.max(4, v ?? 24) } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>最大高度</Text>
                <InputNumber size="small" min={0.5} step={0.5} style={{ width: '100%' }} value={chart.surface.maxHeight} onChange={(v) => update({ surface: { ...chart.surface, maxHeight: v ?? 6 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>分布范围</Text>
                <InputNumber size="small" min={1} step={1} style={{ width: '100%' }} value={chart.surface.spread} onChange={(v) => update({ surface: { ...chart.surface, spread: v ?? 12 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>不透明度</Text>
                <InputNumber size="small" min={0.05} max={1} step={0.05} style={{ width: '100%' }} value={chart.surface.opacity} onChange={(v) => update({ surface: { ...chart.surface, opacity: v ?? 1 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>线框</Text>
                <Switch size="small" checked={chart.surface.wireframe} onChange={(v) => update({ surface: { ...chart.surface, wireframe: v } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>调色板</Text>
                <Input size="small" value={chart.surface.palette.join(',')} onChange={(e) => update({ surface: { ...chart.surface, palette: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) } })} />
              </div>
            </div>
          </>
        )}

        {chart.kind === 'line3d' && (
          <>
            <Divider style={{ margin: '4px 0' }} />
            <Text strong style={{ fontSize: 12 }}>折线</Text>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>线宽</Text>
                <InputNumber size="small" min={0.5} step={0.5} style={{ width: '100%' }} value={chart.line.lineWidth} onChange={(v) => update({ line: { ...chart.line, lineWidth: v ?? 2.5 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>颜色</Text>
                <Input size="small" value={chart.line.color} onChange={(e) => update({ line: { ...chart.line, color: e.target.value } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>最大高度</Text>
                <InputNumber size="small" min={0.5} step={0.5} style={{ width: '100%' }} value={chart.line.maxHeight} onChange={(v) => update({ line: { ...chart.line, maxHeight: v ?? 6 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>分布范围</Text>
                <InputNumber size="small" min={1} step={1} style={{ width: '100%' }} value={chart.line.spread} onChange={(v) => update({ line: { ...chart.line, spread: v ?? 12 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>显示节点</Text>
                <Switch size="small" checked={chart.line.showPoints} onChange={(v) => update({ line: { ...chart.line, showPoints: v } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>节点大小</Text>
                <InputNumber size="small" min={0.05} step={0.05} style={{ width: '100%' }} value={chart.line.pointSize} onChange={(v) => update({ line: { ...chart.line, pointSize: v ?? 0.18 } })} />
              </div>
            </div>
          </>
        )}

        {(chart.kind === 'pie3d' || chart.kind === 'donut3d') && (
          <>
            <Divider style={{ margin: '4px 0' }} />
            <Text strong style={{ fontSize: 12 }}>{chart.kind === 'donut3d' ? '环形' : '饼图'}</Text>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>半径</Text>
                <InputNumber size="small" min={0.5} step={0.5} style={{ width: '100%' }} value={chart.pie.radius} onChange={(v) => update({ pie: { ...chart.pie, radius: v ?? 4.5 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>厚度</Text>
                <InputNumber size="small" min={0.05} step={0.1} style={{ width: '100%' }} value={chart.pie.height} onChange={(v) => update({ pie: { ...chart.pie, height: v ?? 1.2 } })} />
              </div>
              {chart.kind === 'donut3d' && (
                <div>
                  <Text style={{ fontSize: 11, color: '#888' }}>内半径</Text>
                  <InputNumber size="small" min={0} step={0.1} style={{ width: '100%' }} value={chart.pie.innerRadius} onChange={(v) => update({ pie: { ...chart.pie, innerRadius: Math.max(0, v ?? 0) } })} />
                </div>
              )}
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>扇区间隙</Text>
                <InputNumber size="small" min={0} max={0.2} step={0.01} style={{ width: '100%' }} value={chart.pie.gap} onChange={(v) => update({ pie: { ...chart.pie, gap: Math.max(0, v ?? 0.03) } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>不透明度</Text>
                <InputNumber size="small" min={0.05} max={1} step={0.05} style={{ width: '100%' }} value={chart.pie.opacity} onChange={(v) => update({ pie: { ...chart.pie, opacity: v ?? 1 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>调色板</Text>
                <Input size="small" value={chart.pie.palette.join(',')} onChange={(e) => update({ pie: { ...chart.pie, palette: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) } })} />
              </div>
            </div>
          </>
        )}

        {chart.kind === 'gauge3d' && (
          <>
            <Divider style={{ margin: '4px 0' }} />
            <Text strong style={{ fontSize: 12 }}>仪表盘</Text>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>当前值</Text>
                <InputNumber size="small" style={{ width: '100%' }} value={chart.gauge.value} onChange={(v) => update({ gauge: { ...chart.gauge, value: v ?? 0 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>最大值</Text>
                <InputNumber size="small" style={{ width: '100%' }} value={chart.gauge.max} onChange={(v) => update({ gauge: { ...chart.gauge, max: v ?? 100 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>最小值</Text>
                <InputNumber size="small" style={{ width: '100%' }} value={chart.gauge.min} onChange={(v) => update({ gauge: { ...chart.gauge, min: v ?? 0 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>半径</Text>
                <InputNumber size="small" min={0.5} step={0.5} style={{ width: '100%' }} value={chart.gauge.radius} onChange={(v) => update({ gauge: { ...chart.gauge, radius: v ?? 4.2 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>环宽</Text>
                <InputNumber size="small" min={0.1} step={0.1} style={{ width: '100%' }} value={chart.gauge.thickness} onChange={(v) => update({ gauge: { ...chart.gauge, thickness: v ?? 0.85 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>进度颜色</Text>
                <Input size="small" value={chart.gauge.color} onChange={(e) => update({ gauge: { ...chart.gauge, color: e.target.value } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>起始角°</Text>
                <InputNumber size="small" style={{ width: '100%' }} value={chart.gauge.startAngle} onChange={(v) => update({ gauge: { ...chart.gauge, startAngle: v ?? -220 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>结束角°</Text>
                <InputNumber size="small" style={{ width: '100%' }} value={chart.gauge.endAngle} onChange={(v) => update({ gauge: { ...chart.gauge, endAngle: v ?? 40 } })} />
              </div>
            </div>
          </>
        )}

        {chart.kind === 'radar3d' && (
          <>
            <Divider style={{ margin: '4px 0' }} />
            <Text strong style={{ fontSize: 12 }}>雷达</Text>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>半径</Text>
                <InputNumber size="small" min={0.5} step={0.5} style={{ width: '100%' }} value={chart.radar.radius} onChange={(v) => update({ radar: { ...chart.radar, radius: v ?? 4.5 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>环数</Text>
                <InputNumber size="small" min={1} max={10} style={{ width: '100%' }} value={chart.radar.levels} onChange={(v) => update({ radar: { ...chart.radar, levels: Math.max(1, v ?? 4) } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>填充透明度</Text>
                <InputNumber size="small" min={0} max={1} step={0.05} style={{ width: '100%' }} value={chart.radar.fillOpacity} onChange={(v) => update({ radar: { ...chart.radar, fillOpacity: v ?? 0.28 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>颜色</Text>
                <Input size="small" value={chart.radar.color} onChange={(e) => update({ radar: { ...chart.radar, color: e.target.value } })} />
              </div>
            </div>
          </>
        )}

        {chart.kind === 'funnel3d' && (
          <>
            <Divider style={{ margin: '4px 0' }} />
            <Text strong style={{ fontSize: 12 }}>漏斗</Text>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>段高</Text>
                <InputNumber size="small" min={0.2} step={0.1} style={{ width: '100%' }} value={chart.funnel.height} onChange={(v) => update({ funnel: { ...chart.funnel, height: v ?? 1.1 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>顶部宽度</Text>
                <InputNumber size="small" min={0.5} step={0.5} style={{ width: '100%' }} value={chart.funnel.topWidth} onChange={(v) => update({ funnel: { ...chart.funnel, topWidth: v ?? 7 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>段间隙</Text>
                <InputNumber size="small" min={0} step={0.02} style={{ width: '100%' }} value={chart.funnel.gap} onChange={(v) => update({ funnel: { ...chart.funnel, gap: Math.max(0, v ?? 0.14) } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>不透明度</Text>
                <InputNumber size="small" min={0.05} max={1} step={0.05} style={{ width: '100%' }} value={chart.funnel.opacity} onChange={(v) => update({ funnel: { ...chart.funnel, opacity: v ?? 0.95 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>调色板</Text>
                <Input size="small" value={chart.funnel.palette.join(',')} onChange={(e) => update({ funnel: { ...chart.funnel, palette: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) } })} />
              </div>
            </div>
          </>
        )}

        {chart.kind === 'board3d' && (
          <>
            <Divider style={{ margin: '4px 0' }} />
            <Text strong style={{ fontSize: 12 }}>贴屏看板</Text>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>图表类型</Text>
                <Select
                  size="small"
                  style={{ width: '100%' }}
                  value={chart.board.chartType}
                  options={[
                    { value: 'line-chart', label: '折线' },
                    { value: 'bar-chart', label: '柱状' },
                    { value: 'pie-chart', label: '饼图' },
                    { value: 'scatter-3d', label: '3D 散点' },
                    { value: 'surface-3d', label: '3D 曲面' },
                  ]}
                  onChange={(value) => update({ board: { ...chart.board, chartType: value } }, true)}
                />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>分辨率</Text>
                <Select
                  size="small"
                  style={{ width: '100%' }}
                  value={chart.board.resolution}
                  options={[
                    { value: 512, label: '512' },
                    { value: 1024, label: '1024' },
                    { value: 2048, label: '2048' },
                  ]}
                  onChange={(value) => update({ board: { ...chart.board, resolution: value as 512 | 1024 | 2048 } }, true)}
                />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>宽度</Text>
                <InputNumber size="small" min={1} step={0.5} style={{ width: '100%' }} value={chart.board.width} onChange={(v) => update({ board: { ...chart.board, width: v ?? 10 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>高度</Text>
                <InputNumber size="small" min={0.5} step={0.5} style={{ width: '100%' }} value={chart.board.height} onChange={(v) => update({ board: { ...chart.board, height: v ?? 6 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>刷新 Hz</Text>
                <InputNumber size="small" min={1} max={4} step={1} style={{ width: '100%' }} value={chart.board.updateHz} onChange={(v) => update({ board: { ...chart.board, updateHz: Math.max(1, Math.min(4, v ?? 2)) } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>不透明度</Text>
                <InputNumber size="small" min={0.05} max={1} step={0.05} style={{ width: '100%' }} value={chart.board.opacity} onChange={(v) => update({ board: { ...chart.board, opacity: v ?? 1 } })} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Text style={{ fontSize: 11, color: '#888' }}>朝向相机</Text>
                <Switch size="small" checked={chart.board.billboard} onChange={(v) => update({ board: { ...chart.board, billboard: v } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>背景色</Text>
                <Input size="small" value={chart.board.background} onChange={(e) => update({ board: { ...chart.board, background: e.target.value } })} />
              </div>
            </div>
          </>
        )}

        {chart.kind === 'map3d' && (
          <>
            <Divider style={{ margin: '4px 0' }} />
            <Text strong style={{ fontSize: 12 }}>3D 地图</Text>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <Text style={{ fontSize: 11, color: '#888' }}>GeoJSON URL</Text>
                <Input size="small" value={chart.map.geoUrl} onChange={(e) => update({ map: { ...chart.map, geoUrl: e.target.value } }, true)} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>尺寸</Text>
                <InputNumber size="small" min={1} step={1} style={{ width: '100%' }} value={chart.map.scale} onChange={(v) => update({ map: { ...chart.map, scale: v ?? 20 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>挤出高度</Text>
                <InputNumber size="small" min={0.01} step={0.1} style={{ width: '100%' }} value={chart.map.extrude} onChange={(v) => update({ map: { ...chart.map, extrude: v ?? 0.8 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>颜色</Text>
                <Input size="small" value={chart.map.color} onChange={(e) => update({ map: { ...chart.map, color: e.target.value } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>不透明度</Text>
                <InputNumber size="small" min={0.05} max={1} step={0.05} style={{ width: '100%' }} value={chart.map.opacity} onChange={(v) => update({ map: { ...chart.map, opacity: v ?? 0.95 } })} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Text style={{ fontSize: 11, color: '#888' }}>显示边界</Text>
                <Switch size="small" checked={chart.map.showBorders} onChange={(v) => update({ map: { ...chart.map, showBorders: v } })} />
              </div>
            </div>
          </>
        )}

        {chart.kind === 'globe3d' && (
          <>
            <Divider style={{ margin: '4px 0' }} />
            <Text strong style={{ fontSize: 12 }}>3D 地球</Text>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <Text style={{ fontSize: 11, color: '#888' }}>贴图 URL</Text>
                <Input size="small" value={chart.globe.textureUrl} onChange={(e) => update({ globe: { ...chart.globe, textureUrl: e.target.value } }, true)} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>半径</Text>
                <InputNumber size="small" min={0.5} step={0.5} style={{ width: '100%' }} value={chart.globe.radius} onChange={(v) => update({ globe: { ...chart.globe, radius: v ?? 5 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>点位数</Text>
                <InputNumber size="small" min={1} max={500} style={{ width: '100%' }} value={chart.globe.pointCount} onChange={(v) => update({ globe: { ...chart.globe, pointCount: Math.max(1, v ?? 24) } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>点位颜色</Text>
                <Input size="small" value={chart.globe.pointColor} onChange={(e) => update({ globe: { ...chart.globe, pointColor: e.target.value } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>旋转速度</Text>
                <InputNumber size="small" min={0} max={2} step={0.02} style={{ width: '100%' }} value={chart.globe.rotateSpeed} onChange={(v) => update({ globe: { ...chart.globe, rotateSpeed: v ?? 0.08 } })} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Text style={{ fontSize: 11, color: '#888' }}>自动旋转</Text>
                <Switch size="small" checked={chart.globe.autoRotate} onChange={(v) => update({ globe: { ...chart.globe, autoRotate: v } })} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Text style={{ fontSize: 11, color: '#888' }}>大气层</Text>
                <Switch size="small" checked={chart.globe.atmosphere} onChange={(v) => update({ globe: { ...chart.globe, atmosphere: v } })} />
              </div>
            </div>
          </>
        )}

        {chart.kind === 'graph3d' && (
          <>
            <Divider style={{ margin: '4px 0' }} />
            <Text strong style={{ fontSize: 12 }}>3D 关系图</Text>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>节点大小</Text>
                <InputNumber size="small" min={0.05} step={0.05} style={{ width: '100%' }} value={chart.graph.nodeSize} onChange={(v) => update({ graph: { ...chart.graph, nodeSize: v ?? 0.35 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>连线宽度</Text>
                <InputNumber size="small" min={0.2} step={0.2} style={{ width: '100%' }} value={chart.graph.linkWidth} onChange={(v) => update({ graph: { ...chart.graph, linkWidth: v ?? 1.2 } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>布局</Text>
                <Select
                  size="small"
                  style={{ width: '100%' }}
                  value={chart.graph.layout}
                  options={[{ value: 'sphere', label: '球面' }, { value: 'grid', label: '网格' }]}
                  onChange={(value) => update({ graph: { ...chart.graph, layout: value as 'sphere' | 'grid' } }, true)}
                />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>节点色</Text>
                <Input size="small" value={chart.graph.nodeColor} onChange={(e) => update({ graph: { ...chart.graph, nodeColor: e.target.value } })} />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#888' }}>连线色</Text>
                <Input size="small" value={chart.graph.linkColor} onChange={(e) => update({ graph: { ...chart.graph, linkColor: e.target.value } })} />
              </div>
            </div>
          </>
        )}

        <Divider style={{ margin: '4px 0' }} />
        <Text strong style={{ fontSize: 12 }}>主题 / 状态色 / 描边</Text>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>主题</Text>
            <Select
              size="small"
              style={{ width: '100%' }}
              value={chart.appearance?.theme ?? 'tech'}
              options={[
                { value: 'tech', label: '科技' },
                { value: 'dark', label: '暗色' },
                { value: 'light', label: '亮色' },
              ]}
              onChange={(value) => update({ appearance: { ...chart.appearance, theme: value as Chart3DObject['appearance']['theme'] } }, true)}
            />
          </div>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>强调色</Text>
            <Input size="small" value={chart.appearance?.accent ?? '#00d8ff'} onChange={(e) => update({ appearance: { ...chart.appearance, accent: e.target.value } })} />
          </div>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>选中描边</Text>
            <Switch size="small" checked={chart.appearance?.outline !== false} onChange={(v) => update({ appearance: { ...chart.appearance, outline: v } })} />
          </div>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>描边色</Text>
            <Input size="small" value={chart.appearance?.outlineColor ?? '#ffd666'} onChange={(e) => update({ appearance: { ...chart.appearance, outlineColor: e.target.value } })} />
          </div>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>状态阈值 &gt;</Text>
            <InputNumber
              size="small"
              style={{ width: '100%' }}
              value={chart.appearance?.statusRules?.[0]?.threshold}
              onChange={(v) => {
                const current = chart.appearance?.statusRules?.[0];
                const rules = v === null || v === undefined
                  ? []
                  : [{ metric: 'value' as const, op: '>' as const, threshold: v, color: current?.color || '#ff4d4f' }];
                update({ appearance: { ...chart.appearance, statusRules: rules } });
              }}
            />
          </div>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>状态色</Text>
            <Input
              size="small"
              value={chart.appearance?.statusRules?.[0]?.color ?? '#ff4d4f'}
              onChange={(e) => {
                const current = chart.appearance?.statusRules?.[0];
                const rules = current
                  ? [{ ...current, color: e.target.value }]
                  : [{ metric: 'value' as const, op: '>' as const, threshold: 80, color: e.target.value }];
                update({ appearance: { ...chart.appearance, statusRules: rules } });
              }}
            />
          </div>
        </div>

        <Divider style={{ margin: '4px 0' }} />
        <Text strong style={{ fontSize: 12 }}>坐标轴 / 图例 / 标签 / 动画</Text>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Text style={{ fontSize: 11, color: '#888' }}>坐标轴</Text>
            <Switch size="small" checked={chart.axes.show} onChange={(v) => update({ axes: { ...chart.axes, show: v } })} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Text style={{ fontSize: 11, color: '#888' }}>图例</Text>
            <Switch size="small" checked={chart.legend.show} onChange={(v) => update({ legend: { ...chart.legend, show: v } })} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Text style={{ fontSize: 11, color: '#888' }}>值标签</Text>
            <Switch size="small" checked={chart.labels.show} onChange={(v) => update({ labels: { ...chart.labels, show: v } })} />
          </div>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>标签上限</Text>
            <InputNumber size="small" min={0} max={200} style={{ width: '100%' }} value={chart.labels.max} onChange={(v) => update({ labels: { ...chart.labels, max: Math.max(0, v ?? 0) } })} />
          </div>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>X 轴名</Text>
            <Input size="small" value={chart.axes.xLabel} onChange={(e) => update({ axes: { ...chart.axes, xLabel: e.target.value } })} />
          </div>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>Y 轴名</Text>
            <Input size="small" value={chart.axes.yLabel} onChange={(e) => update({ axes: { ...chart.axes, yLabel: e.target.value } })} />
          </div>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>Z 轴名</Text>
            <Input size="small" value={chart.axes.zLabel} onChange={(e) => update({ axes: { ...chart.axes, zLabel: e.target.value } })} />
          </div>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>轴颜色</Text>
            <Input size="small" value={chart.axes.color} onChange={(e) => update({ axes: { ...chart.axes, color: e.target.value } })} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Text style={{ fontSize: 11, color: '#888' }}>生长动画</Text>
            <Switch size="small" checked={chart.animation.enabled && chart.animation.grow} onChange={(v) => update({ animation: { ...chart.animation, enabled: v, grow: v } })} />
          </div>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>时长 ms</Text>
            <InputNumber size="small" min={120} step={100} style={{ width: '100%' }} value={chart.animation.durationMs} onChange={(v) => update({ animation: { ...chart.animation, durationMs: Math.max(120, v ?? 900) } })} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Text style={{ fontSize: 11, color: '#888' }}>循环</Text>
            <Switch size="small" checked={chart.animation.loop} onChange={(v) => update({ animation: { ...chart.animation, loop: v } })} />
          </div>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>自转速度</Text>
            <InputNumber
              size="small"
              min={0}
              max={2}
              step={0.02}
              style={{ width: '100%' }}
              value={chart.animation.spinSpeed}
              onChange={(v) => update({ animation: { ...chart.animation, spinSpeed: Math.max(0, v ?? 0) } })}
            />
          </div>
        </div>

        <Divider style={{ margin: '4px 0' }} />
        <Text strong style={{ fontSize: 12 }}>示例数据（无数据源时使用）</Text>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>柱数</Text>
            <InputNumber
              data-testid="chart3d-demo-count"
              size="small"
              min={1}
              max={4000}
              style={{ width: '100%' }}
              value={chart.demo.count}
              onChange={(v) => update({ demo: { ...chart.demo, count: Math.max(1, v ?? 1) } })}
            />
          </div>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>随机种子</Text>
            <InputNumber
              size="small"
              style={{ width: '100%' }}
              value={chart.demo.seed}
              onChange={(v) => update({ demo: { ...chart.demo, seed: v ?? 1 } })}
            />
          </div>
        </div>

        <Divider style={{ margin: '4px 0' }} />
        <Text strong style={{ fontSize: 12 }}>数据源（可选）</Text>
        <Text style={{ fontSize: 11, color: '#888' }}>
          支持 static/api/websocket/sql/mqtt/device 的 DataSourceConfig JSON；留空使用示例数据。
        </Text>
        <Input.TextArea
          data-testid="chart3d-data-source"
          rows={6}
          value={dataSourceText}
          placeholder='{"type":"static","config":{"data":[{"label":"A","value":30}]}}'
          onChange={(e) => setDataSourceText(e.target.value)}
        />
        {dataSourceError && <Alert type="error" showIcon message={dataSourceError} />}
        <Space>
          <Button size="small" type="primary" onClick={applyDataSource} data-testid="chart3d-apply-data-source">
            应用数据源
          </Button>
          <Button
            size="small"
            data-testid="chart3d-datasource-form-toggle"
            onClick={() => setShowDataSourceForm((v) => !v)}
          >
            {showDataSourceForm ? '收起可视化配置' : '可视化配置（设备/数据集）'}
          </Button>
          <Button
            size="small"
            onClick={() => {
              setDataSourceText('');
              setDataSourceError(null);
              update({ dataSource: undefined }, true);
            }}
          >
            清空（用示例）
          </Button>
        </Space>

        {showDataSourceForm && (
          <div data-testid="chart3d-datasource-form" style={{ border: '1px solid #2a2a3e', borderRadius: 6, padding: 8 }}>
            <DataSourceConfigForm
              value={chart.dataSource}
              widgetType={chart.kind}
              onChange={(ds) => {
                setDataSourceText(JSON.stringify(ds, null, 2));
                setDataSourceError(null);
                update({ dataSource: ds }, true);
              }}
            />
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>类目字段</Text>
            <Input
              size="small"
              value={chart.dataMapping?.category ?? ''}
              placeholder="label"
              onChange={(e) => update({ dataMapping: { ...chart.dataMapping, category: e.target.value || undefined } })}
            />
          </div>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>数值字段</Text>
            <Input
              size="small"
              value={chart.dataMapping?.value ?? ''}
              placeholder="value"
              onChange={(e) => update({ dataMapping: { ...chart.dataMapping, value: e.target.value || undefined } })}
            />
          </div>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>X 字段</Text>
            <Input
              size="small"
              value={chart.dataMapping?.x ?? ''}
              placeholder="x"
              onChange={(e) => update({ dataMapping: { ...chart.dataMapping, x: e.target.value || undefined } })}
            />
          </div>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>Y 字段</Text>
            <Input
              size="small"
              value={chart.dataMapping?.y ?? ''}
              placeholder="y"
              onChange={(e) => update({ dataMapping: { ...chart.dataMapping, y: e.target.value || undefined } })}
            />
          </div>
          <div>
            <Text style={{ fontSize: 11, color: '#888' }}>Z 字段</Text>
            <Input
              size="small"
              value={chart.dataMapping?.z ?? ''}
              placeholder="z"
              onChange={(e) => update({ dataMapping: { ...chart.dataMapping, z: e.target.value || undefined } })}
            />
          </div>
        </div>

        <Button danger size="small" block onClick={() => { removeChart3D(chart.id); useSelectionStore.getState().selectChart3D(null); }}>
          删除此 3D 图表
        </Button>
      </Space>
    </div>
  );
}
